<template>
    <b-container>
        <div v-if="error" class="pt-4">
            <b-alert show variant="danger">
                <p>{{ error }}</p>
            </b-alert>
        </div>

        <h5 class="mt-4">{{ $t('info.currentOwner') }}</h5>
        <hr>
        <b-row>
            <b-col sm="8">
                <v-select v-model="ownerToAdd" :options="users" @search="searchUser"
                    :placeholder="$t('action.chooseUser')" label="displayName">
                </v-select>
            </b-col>
            <b-col sm="4">
                <b-button variant="success" @click="addOwner" :disabled="!ownerToAdd">{{ $t('action.add') }}</b-button>
            </b-col>
        </b-row>

        <b-table id="members-table" striped hover :items="ownersList" :fields="fields" class="mt-3" :per-page="perPage"
            :current-page="currentPage">
            <template #cell(actions)="data">
                <b-button variant="outline-danger" size="sm" @click="showRemoveConfirmation(data.item, 'owners')">
                    {{ $t("action.remove") }}
                </b-button>
            </template>
        </b-table>

        <b-row v-if="ownersList.length > perPage">
            <b-col>
                <b-pagination v-model="currentPage" :total-rows="ownersList.length" :per-page="perPage"
                    aria-controls="owners-table" align="center" class="mt-3">
                </b-pagination>
            </b-col>
        </b-row>

        <hr>

        <h5 class="mt-4">{{ $t('info.currentTeamMember') }}</h5>
        <hr>
        <b-row>
            <b-col sm="8">
                <v-select v-model="userToAdd" :options="users" @search="searchUser"
                    :placeholder="$t('action.chooseUser')" label="displayName">
                </v-select>
            </b-col>
            <b-col sm="4">
                <b-button variant="success" @click="addMember" :disabled="!userToAdd">{{ $t('action.add') }}</b-button>
            </b-col>
        </b-row>

        <b-table id="members-table" striped hover :items="membersList" :fields="fields" class="mt-3" :per-page="perPage"
            :current-page="currentPage">
            <template #cell(actions)="data">
                <b-button variant="outline-danger" size="sm" @click="showRemoveConfirmation(data.item, 'members')">
                    {{ $t("action.remove") }}
                </b-button>
            </template>
        </b-table>

        <b-row v-if="membersList.length > perPage">
            <b-col>
                <b-pagination v-model="currentPage" :total-rows="membersList.length" :per-page="perPage"
                    aria-controls="members-table" align="center" class="mt-3">
                </b-pagination>
            </b-col>
        </b-row>

        <div v-if="spinner" class="text-center">
            <b-spinner style="width: 3rem; height: 3rem" class="spinner" variant="success" label="Spinning"></b-spinner>
        </div>

        <b-modal id="remove-member-modal" :title="$t('title.confirmation')" hide-footer>
            <p>{{ $t("action.rmve") }} <b>{{ memberToRemove ? memberToRemove.displayName : '' }}</b> ?</p>
            <b-button variant="danger" @click="confirmRemoveMember">{{ $t("action.remove") }}</b-button>
            <b-button variant="secondary" class="ml-2" @click="$bvModal.hide('remove-member-modal')">{{
                $t("action.cncl")
                }}</b-button>
        </b-modal>
    </b-container>
</template>

<script>
import axios from "axios";
import { globalF } from "../../Helpers";

export default {
    mixins: [globalF],
    props: ["rowItem", "modalid"],
    data() {
        return {
            membersList: [],
            ownersList: [],
            spinner: false,
            error: "",
            users: [],
            userToAdd: null,
            ownerToAdd: null,
            perPage: 10,
            memberToRemove: null,
            currentPage: 1,
            fields: [
                { key: 'displayName', label: this.$t('label.fullname') },
                { key: 'mail', label: this.$t('label.email') },
                { key: 'actions', label: 'Action' }
            ]
        };
    },
    mounted() {
        this.$root.$on('bv::modal::hide', () => {
            this.fetchGroupMembers();
        });
        this.fetchGroupMembers();
    },
    methods: {
        fetchGroupMembers() {
            this.spinner = true;
            let accessToken = this.getGlobalData("accessToken");
            let groupId = this.rowItem.GroupId;
            let graphEndpoint = `https://graph.microsoft.com/v1.0/groups/${groupId}/members`;
            axios.get(graphEndpoint, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            }).then((response) => {
                this.membersList = response.data.value;
                let graphOwnersEndpoint = `https://graph.microsoft.com/v1.0/groups/${groupId}/owners`;
                this.fetchOwners(graphOwnersEndpoint, accessToken);
            }).catch(() => {
                this.spinner = false;
                this.error = this.$t("info.errorgeneric");
            });
        },
        fetchOwners(graphEndpoint, accessToken) {
            axios.get(graphEndpoint, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            }).then((response) => {
                this.ownersList = response.data.value;
                this.spinner = false;
            }).catch(() => {
                this.spinner = false;
                this.error = this.$t("info.errorgeneric");
            });
        },
        searchUser(searchText) {
            if (searchText) {
                let token = this.getGlobalData("accessToken");
                this.spinner = true;
                let graphEndpoint = `https://graph.microsoft.com/v1.0/users?$filter=startswith(displayName,'${searchText}')&$count=true&$top=10`;
                axios.get(graphEndpoint, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                        ConsistencyLevel: "eventual",
                    },
                }).then((response) => {
                    this.spinner = false;
                    if (response.status === 200) {
                        this.users = response.data.value;
                    } else {
                        this.error = this.$t("info.errorgeneric");
                    }
                }).catch(() => {
                    this.error = this.$t("info.errorgeneric");
                    this.spinner = false;
                });
            }
        },
        addOwner() {
            if (!this.ownerToAdd) return;
            this.spinner = true;
            let token = this.getGlobalData("accessToken");
            let groupId = this.rowItem.GroupId;

            let graphEndpoint = `https://graph.microsoft.com/v1.0/groups/${groupId}/owners/$ref`;
            let body = { "@odata.id": `https://graph.microsoft.com/v1.0/users/${this.ownerToAdd.id}` };

            axios.post(graphEndpoint, body, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            }).then(() => {
                this.ownerToAdd = null;
                this.fetchGroupMembers();
                this.$bvToast.toast(this.$t("info.userAddedSuccess"), {
                    title: this.$t("title.success"),
                    variant: "success",
                    solid: true,
                });
                this.$emit('members-updated');
            }).catch(() => {
                this.spinner = false;
                this.error = this.$t("info.errorgeneric");
            });
        },
        addMember() {
            if (!this.userToAdd) return;
            this.spinner = true;
            let token = this.getGlobalData("accessToken");
            let groupId = this.rowItem.GroupId;

            let graphEndpoint = `https://graph.microsoft.com/v1.0/groups/${groupId}/members/$ref`;
            let body = { "@odata.id": `https://graph.microsoft.com/v1.0/users/${this.userToAdd.id}` };

            axios.post(graphEndpoint, body, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            }).then(() => {
                this.userToAdd = null;
                this.fetchGroupMembers();
                this.$bvToast.toast(this.$t("info.userAddedSuccess"), {
                    title: this.$t("title.success"),
                    variant: "success",
                    solid: true,
                });
                this.$emit('members-updated');
            }).catch(() => {
                this.spinner = false;
                this.error = this.$t("info.errorgeneric");
            });
        },
        showRemoveConfirmation(member, membertype) {
            this.memberToRemove = member;
            this.memberType = membertype;
            this.$bvModal.show("remove-member-modal");
        },
        confirmRemoveMember() {
            this.removeMember(this.memberToRemove);
            this.$bvModal.hide("remove-member-modal");
        },
        removeMember(member) {
            if (!member) return;

            this.spinner = true;
            let token = this.getGlobalData("accessToken");
            let groupId = this.rowItem.GroupId;
            let memberId = member.id;
            let graphEndpoint = `https://graph.microsoft.com/v1.0/groups/${groupId}/members/${memberId}/$ref`;
            if (this.memberType === 'owners') {
                graphEndpoint = `https://graph.microsoft.com/v1.0/groups/${groupId}/owners/${memberId}/$ref`;
            }

            axios.delete(graphEndpoint, {
                headers: { Authorization: `Bearer ${token}` },
            }).then(() => {
                this.fetchGroupMembers();
                this.memberToRemove = null;
                this.$bvToast.toast(this.$t("info.userRemovedSuccess"), {
                    title: this.$t("title.success"),
                    variant: "success",
                    solid: true,
                });
                this.$emit('members-updated');
            }).catch(() => {
                this.spinner = false;
                this.error = this.$t("info.errorgeneric");
            });
        },
    },
};
</script>